// client/src/utils/request.js
import axios from 'axios';

// 判断是否在小程序环境
const isMiniProgram = () => {
  return typeof window !== 'undefined' && window.__wxjs_environment === 'miniprogram';
};

// 判断是否在微信内
const isWeChat = () => {
  return typeof window !== 'undefined' && /MicroMessenger/i.test(navigator.userAgent);
};

// 获取openid的函数 - 新增
// client/src/utils/request.js - 增强版 getOpenId
const persistAuthFromUrl = () => {
  if (typeof window === 'undefined') return null;
  const url = new URL(window.location.href);
  const t = url.searchParams.get('t');
  const openidParam = url.searchParams.get('openid');
  if (!t && !openidParam) return null;

  if (openidParam) {
    try {
      localStorage.setItem('openid', openidParam);
    } catch (e) {
      console.warn('无法写入 openid:', e?.message);
    }
    url.searchParams.delete('openid');
  }
  if (t) {
    try {
      localStorage.setItem('openid_token', t);
    } catch (e) {
      console.warn('无法写入 openid_token:', e?.message);
    }
    url.searchParams.delete('t');
  }
  window.history.replaceState({}, '', url.toString());
  return t || null;
};

const getOpenidToken = () => {
  // 1) t / openid 在地址栏时写入 localStorage 并清理展示栏
  const fromUrl = persistAuthFromUrl();
  if (fromUrl) return fromUrl;

  // 2) localStorage
  try {
    return localStorage.getItem('openid_token');
  } catch {
    return null;
  }
};

const getOpenId = () => {
  console.log('🔍 开始获取 openid...');
  console.log('当前 window.location.search:', window.location.search);
  
  // 1. 优先从 URL 参数获取
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    const openidFromUrl = urlParams.get('openid');
    if (openidFromUrl) {
      console.log('✅ 从URL获取openid成功:', openidFromUrl);
      return openidFromUrl;
    } else {
      console.log('❌ URL中没有找到 openid 参数');
    }
    
    // 2. 其次从 localStorage 获取
    const openidFromStorage = localStorage.getItem('openid');
    if (openidFromStorage) {
      console.log('✅ 从localStorage获取openid:', openidFromStorage);
      return openidFromStorage;
    } else {
      console.log('❌ localStorage中没有 openid');
    }
  }
  
  // 3. 最后使用测试值
  if (process.env.NODE_ENV === 'development') {
    console.log('⚠️ 使用测试openid: test_openid_001');
    return 'test_openid_001';
  }
  
  console.log('❌ 无法获取openid，返回null');
  return null;
};

// 封装请求
const request = async (config) => {
  // 如果在微信小程序环境，用 wx.cloud.callContainer（仅 JSON；FormData 上传须走浏览器/axios multipart）
  if (isMiniProgram() && window.wx && !(config.data instanceof FormData)) {
    return new Promise((resolve, reject) => {
      window.wx.cloud.callContainer({
        path: config.url,
        method: config.method || 'GET',
        header: {
          "X-WX-SERVICE": "tcbanyservice",
          "X-AnyService-Name": "match",
          "content-type": "application/json",
          ...config.headers
        },
        data: config.data,
        success: (res) => resolve({ data: res.data }),
        fail: (err) => reject(err)
      });
    });
  }
  
  // 基础URL - 本地访问用当前域名，否则用环境变量或生产
  const defaultBase = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? window.location.origin
    : 'https://www.ningqianxiaowo.com';
  const baseURL = process.env.REACT_APP_API_URL || defaultBase;
  
  // 确保 config.url 以 /api 开头
  let url = config.url;
  if (!url.startsWith('/api') && !url.startsWith('http')) {
    url = `/api${url.startsWith('/') ? url : '/' + url}`;
  }
  
  // 获取openid
  const openid = getOpenId();
  const token = getOpenidToken();
  
  console.log('📤 请求:', config.method || 'GET', baseURL + url);
  console.log('📦 数据:', config.data || {});
  console.log('🔑 openid:', openid);
  console.log('🎫 token:', token ? token.slice(0, 10) + '...' : null);
  
  // 构建请求头（FormData 时不要设置 Content-Type，让浏览器自动带 boundary）
  const headers = { ...config.headers };
  if (!(config.data instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  
  // 如果有openid，添加到请求头
  if (token) {
    headers['x-wx-openid-token'] = token;
  } else if (openid) {
    headers['x-wx-openid'] = openid;
  }

  console.log("data", config.data);
  console.log("params", config.params);

  const isUpload = config.data instanceof FormData;
  return axios({
    baseURL,
    url,
    method: config.method || 'GET',
    // 形象照压缩 + 移动网络上传易超过 10s
    timeout: isUpload ? 120000 : 10000,
    headers: headers,
    data: config.data,
    params: config.params,
    validateStatus: () => true,
    responseType: 'text'  // 文本接收，避免服务端返回 HTML 时 axios 解析 JSON 报 "parse error invalid response status"
  }).then(response => {
    let data = response.data;
    if (typeof data === 'string') {
      try {
        data = data ? JSON.parse(data) : null;
      } catch {
        data = data;  // 解析失败保留原文（如 HTML 错误页）
      }
    }
    response.data = data;
    console.log('✅ 响应:', response.status, response.data);
    if (response.status >= 400) {
      const errMsg = (data && typeof data === 'object' && data.error) ? data.error
        : (typeof data === 'string' && data.length < 200) ? data
        : `请求失败(${response.status})`;
      const err = new Error(errMsg);
      err.response = { status: response.status, data };
      throw err;
    }
    return response;
  }).catch(error => {
    if (error.response) {
      console.error('❌ 请求失败:', error.config?.url, error.response.status, error.response.data);
    } else {
      console.error('❌ 请求失败:', error.config?.url, error.message);
    }
    throw error;
  });
};

export const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://www.ningqianxiaowo.com';

/** 获取图片资源的 base（本地访问时用当前域名，确保图片能加载） */
function getImageBase() {
  if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
    return window.location.origin;
  }
  return apiBaseUrl.replace(/\/$/, '');
}

/** 活动/用户图片 src：支持 http、data:、相对路径 /uploads/... */
export function getImageSrc(url) {
  if (!url || typeof url !== 'string') return '';
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  const base = getImageBase();
  if (url.startsWith('/')) return `${base}${url}`;
  return `${base}/${url}`;
}

export default request;